import * as React from "react";
import { Form, useFormApi } from "informedv4";
import DocumentTitle from "../../misc/components/DocumentTitle";
import Alert, { AlertCategoryEnum } from "../../misc/components/Alert";
import { Link } from "react-router-dom";
import { useCurrentStudyConfigurationContext } from "../../Core/CurrentStudyConfigurationContext";
import { Authorization } from "../../misc/components/Authorization";
import { useSecurityContext } from "../../Core/SecurityContext";
import { PermissionEnum } from "../../misc/api/ApiPermissionEnum";
import { useReferenceContextProvider } from "../../Core/ReferenceContextProvider";
import { SiteFormBody } from "./SiteFormBody";
import { useWebPortalApiClient, useWebPortalLegacyClient } from "../../misc/api/WebPortalTokenClient";
import { trimInput } from "../../misc/Helpers/StringHelper";

interface SiteFormModel {
    SiteId: string;
    Name: string;
    Address: string;
    Country: string;
    TimeZone: string;
    Contact: string;
    Email: string;
    Phone: string;
}

export const ActionButtons = ({ submitting, automationIdPrefix, onSaveClick }) => {
    return (
        <div className="h2 pull-right">
            <button className="btn btn-primary" type="submit" disabled={submitting} onClick={onSaveClick} data-automationid={`${automationIdPrefix}_Save`}>
                Save
            </button>&nbsp;
            <Link className="btn btn-primary btn-outline" to="/Sites" data-automationid={`${automationIdPrefix}_ReturnToList`}>
                Return to List
            </Link>
        </div>
    );
}

export const CreateSiteForm = () => {
    const { countries, study } = useCurrentStudyConfigurationContext();
    const { timeZones } = useReferenceContextProvider();
    const formApi = useFormApi();
    const [successAlert, setSuccessAlert] = React.useState(false);
    const [submitting, setSubmitting] = React.useState(false);
    const [warningAlert, setWarningAlert] = React.useState<string>(null);
    const [errorAlert, setErrorAlert] = React.useState<string>(null);

    const Client = useWebPortalApiClient();

    const onSubmit = async () => {
        setSubmitting(true);
        setSuccessAlert(false);
        setWarningAlert(null);
        setErrorAlert(null);

        formApi.validate();
        const state = formApi.getFormState();

        if (state.invalid) {
            setSuccessAlert(false);
            setSubmitting(false);
            return setErrorAlert("Errors exist on the page");
        }

        const apiForm = {
            ...state.values,
        }

        try {
            const site = await Client.client.postSites({
                studyId: study.id,
                name: trimInput(apiForm.Name as string),
                number: trimInput(apiForm.SiteId as string),
                address: trimInput(apiForm.Address as string),
                country: apiForm.Country as string,
                timeZone: apiForm.TimeZone as string,
                contact: trimInput(apiForm.Contact as string),
                email: apiForm.Email as string,
                phone: trimInput(apiForm.Phone as string),
            });

            if (site) {
                setSuccessAlert(true);
            }
        } catch (ex) {
            setErrorAlert(ex.message);
        }

        setSubmitting(false);
    };

    return (
        <>
            <Alert show={successAlert} category={AlertCategoryEnum.Success}>
                Saved successfully. Return to{" "}
                <Link to={`/Sites`}>Sites list</Link>?
            </Alert>
            <Alert show={warningAlert !== null} category={AlertCategoryEnum.Warning}>
                {warningAlert}
            </Alert>
            <Alert show={errorAlert !== null} category={AlertCategoryEnum.Danger}>
                {errorAlert}
            </Alert>
            <div className="row">
                <div className="col-md-6">
                    <h2 className="text-primary">Create Site</h2>
                </div>
            </div>
            <div className="row">
                <div className="col-md-12">
                    <div id="detailsPanel" className="panel panel-primary">
                        <div className="panel-heading">
                            <h4>Details</h4>
                        </div>
                        <div className="panel-body">
                            <SiteFormBody />
                        </div>
                    </div>
                </div>
            </div>
            <div className="row">
                <div className="col-md-12">
                    <ActionButtons submitting={submitting} onSaveClick={onSubmit} automationIdPrefix="ActionsFooter" />
                </div>
            </div>
        </>
    );
};

export const CreateSiteScreen = () => {
    const { study } = useCurrentStudyConfigurationContext();
    const { currentUserPermissions } = useSecurityContext();

    return (
        <Authorization studyId={study.id} currentPermissions={currentUserPermissions} permissionIdsAny={[PermissionEnum.CreateSite]}>
            <DocumentTitle title="Create Site | Vitalograph Web Portal">
                <Form validateOn="submit" noValidate>
                    <CreateSiteForm />
                </Form>
            </DocumentTitle>
        </Authorization>
    );
}
